/// <reference types="@types/google.maps" />
import React, { useState, useRef, useEffect, useCallback } from "react";
import LocationIcon from "./LocationIcon";
import useDeepCompareEffect from "use-deep-compare-effect";
import OutsideAlerter from "~/shared/OutsideAlerter";
import { FullAddress, Place } from "~/shared/GoogleMapsInput/types";
import { fromPlaceToAddress } from "~/shared/GoogleMapsInput/fromPlaceToAddress";
import { Group, PlaceList } from "~/shared/GoogleMapsInput/styles";

type GoogleMapsInputProps = {
    name: string;
    value: string;
    label: string;
    focus?: boolean;
    error?: string;
    handleChange: (name: string, value: FullAddress) => void;
    handleBlur: (name: string, value: FullAddress) => void;
    [key: string]: any;
};

let sessionToken = null;

const refreshMapsSessionToken = () => {
    sessionToken = new google.maps.places.AutocompleteSessionToken();
};

const GoogleMapsInput = ({
    name,
    value,
    label,
    handleChange,
    handleBlur,
    error = "",
    ...props
}: GoogleMapsInputProps) => {
    const [fullAddress, setFullAddress] = useState(value || "");
    const [innerError, setInnerError] = useState(error);
    const [optionSelected, setOptionSelected] = useState(false);
    const [preds, setPredictions] = useState([]);
    const autocompleteRef = useRef<google.maps.places.AutocompleteService>(null);
    const mapsInputRef = useRef();
    const [place, setPlace] = useState(null);

    useEffect(() => {
        refreshMapsSessionToken();
        autocompleteRef.current = new google.maps.places.AutocompleteService();
    }, []);

    const displaySuggestions = useCallback((predictions, status) => {
        // @ts-ignore-next-line
        if (status != google.maps.places.PlacesServiceStatus.OK || !predictions) {
            console.error(status);
            return;
        }

        setPredictions(predictions);
    }, []);

    useEffect(() => {
        setInnerError(error);
    }, [error]);

    useEffect(() => {
        setFullAddress(value);
    }, [value]);

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const eventValue = event.target.value;
        setFullAddress(eventValue);
        handleChange(name, {});
        handleBlur(name, {});
        setOptionSelected(false);

        if (autocompleteRef.current) {
            autocompleteRef.current.getPlacePredictions({
                input: eventValue,
                sessionToken: sessionToken,
                componentRestrictions: { country: "br" },
            }, displaySuggestions);
        }
    };

    const onBlur = () => {
        if (!optionSelected) {
            setInnerError("Selecione uma das opções da lista de endereço");
        }
    };

    const fetchPlace = useCallback((placeId: string) => {
        const fullSelectedAddress = preds?.find(item => item.place_id === placeId)?.description ?? "";
        const service = new google.maps.places.PlacesService(document.createElement('div'));
        const request = {
            placeId,
            fields: ["address_components", "formatted_address", "geometry"],
            sessionToken: sessionToken
        };

        service.getDetails(request, (result: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
            refreshMapsSessionToken();
            if (status != google.maps.places.PlacesServiceStatus.OK || !result) {
                console.error(status);
                return;
            }

            const currentPlace = fromPlaceToAddress(result as Place);
            setFullAddress(fullSelectedAddress);
            setPlace(currentPlace);
            setPredictions([]);
        });
    }, []);

    const onSelectPlace = useCallback((address: FullAddress | null) => {
        if (!address) {
            setInnerError("Selecione uma das opções da lista de endereço");
            return;
        }
        setOptionSelected(true);
        setInnerError(() => "");
        handleChange(name, address);
        handleBlur(name, address);
    }, [handleBlur, handleChange]);

    useDeepCompareEffect(() => {
        if (place) {
            const currentPlace = { ...place };
            setPlace(() => null);
            onSelectPlace(currentPlace);
        }
    }, [place, onSelectPlace]);

    return (
        <Group $error={!!innerError}>
            {label ? (
                <label htmlFor={name}>
                    <span>{label}</span>
                </label>
            ) : (
                ""
            )}
            <div id="ignored" />

            <div className="inputContainer">
                <LocationIcon />

                <input
                    {...props}
                    type="text"
                    id={name}
                    name={name}
                    className="form-control"
                    value={fullAddress}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={mapsInputRef}
                />

                <OutsideAlerter onOutside={() => setPredictions([])}>
                    {preds && preds.length ? (
                        <PlaceList>
                            {preds.map(pred => (
                                <li key={pred.place_id}>
                                    <button type="button" onClick={() => fetchPlace(pred.place_id)}>
                                        <span dangerouslySetInnerHTML={{ __html: pred.description.replace(fullAddress, `<b>${fullAddress}</b>`) }} />
                                    </button>
                                </li>
                            ))}
                            <li className="places-license"><img src="/images/license/google_on_white.png" alt="Dados providos pelo Google" /></li>
                        </PlaceList>
                    ) : (
                        ""
                    )}
                </OutsideAlerter>
            </div>

            {error ? <span className="error">{innerError}</span> : ""}
        </Group>
    );
};

export default GoogleMapsInput;
